import { motion } from "framer-motion";
import { Markdown } from "../markdown";
import type { StepsData } from "../types";

/**
 * Beyond Sure — BI2 grounded step-flow / process guidance.
 *
 * When the user asks "how do I proceed / how to claim / what are the steps", the
 * advisor's (KB-grounded) answer contains a numbered process; the backend lifts
 * those numbered lines VERBATIM into `env.steps` and this component renders them
 * as a calm vertical stepper — the process end-to-end, at a glance.
 *
 * GROUNDED BY CONSTRUCTION: it only re-formats the steps it is given; it never
 * synthesises timelines, amounts, or claim rules. Grounding provenance is carried
 * by the CitationsRow that renders under the same message.
 *
 * Reusable + presentational — no chat-specific context; safe to mount elsewhere.
 */

interface StepFlowProps {
  data: StepsData;
}

const listVariants = { animate: { transition: { staggerChildren: 0.06 } } };
const itemVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

export function StepFlow({ data }: StepFlowProps) {
  const steps = (data.steps || []).map((s) => (s || "").trim()).filter(Boolean);
  // Empty-state: render nothing rather than an empty card (a flow with <2 steps
  // isn't a process — the prose already covers it).
  if (steps.length < 2) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      data-testid="message-step-flow"
      className={[
        "rounded-[14px] overflow-hidden",
        "bg-[rgb(255_255_255_/_0.92)] backdrop-blur-sm",
        "border border-[var(--color-ink-08)]",
        "shadow-[0_6px_22px_-14px_rgba(20,75,51,0.28)]",
      ].join(" ")}
    >
      {/* Header strip */}
      <div className="flex items-center gap-2.5 px-4 sm:px-5 py-2.5 border-b border-[var(--color-ink-08)] bg-[var(--color-paper)]">
        <span
          className="inline-flex items-center justify-center h-7 w-7 rounded-[8px] shrink-0"
          style={{ backgroundColor: "var(--color-brand-soft)" }}
          aria-hidden
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 5h7a4 4 0 0 1 0 8H9a4 4 0 0 0 0 8h9"
              stroke="var(--color-brand-deep)"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="6" cy="5" r="2.2" fill="var(--color-brand-deep)" />
            <circle cx="18" cy="21" r="2.2" fill="var(--color-brand-deep)" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] bsure-caps font-semibold tracking-[0.16em] text-[var(--color-brand-deep)]">
            {data.title || "Step-by-step"}
          </p>
          <p className="text-[12.5px] text-[var(--color-ink-60)]">
            {data.source || "The process, end to end"}
          </p>
        </div>
      </div>

      {/* The stepper */}
      <motion.ol
        variants={listVariants}
        initial="initial"
        animate="animate"
        className="px-4 sm:px-5 py-4 flex flex-col"
        aria-label={data.title || "Step-by-step guidance"}
      >
        {steps.map((step, i) => (
          <motion.li
            key={i}
            variants={itemVariants}
            className="relative flex gap-3 pb-4 last:pb-0"
          >
            {/* connector line to the next node */}
            {i < steps.length - 1 ? (
              <span
                aria-hidden
                className="absolute left-[13px] top-[28px] bottom-0 w-[2px] bg-[var(--color-ink-08)]"
              />
            ) : null}
            <span
              className="relative z-[1] inline-flex items-center justify-center h-[26px] w-[26px] rounded-full shrink-0 text-[12px] font-bold text-white"
              style={{ backgroundColor: "var(--color-brand-deep)" }}
              aria-hidden
            >
              {i + 1}
            </span>
            <div className="pt-[2px] text-[14px] leading-[1.55] text-[var(--color-ink)] min-w-0 bsure-step-body">
              <Markdown source={step} />
            </div>
          </motion.li>
        ))}
      </motion.ol>

      {data.note ? (
        <div className="px-4 sm:px-5 pb-4 -mt-1">
          <p className="text-[12px] leading-[1.5] text-[var(--color-ink-60)] border-l-2 border-[var(--color-brand-soft)] pl-3">
            {data.note}
          </p>
        </div>
      ) : null}
    </motion.div>
  );
}

export default StepFlow;
